import { Form, Button, Checkbox, Spin, Empty, Input } from "antd";
import React, { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { StyledFlex } from "../Table/index.styled";
import styled from "styled-components";

const StyledCheckbox = styled(Checkbox)`
  line-height: 32px;
  width: 100%;
  white-space: nowrap;

  .ant-checkbox-label {
    width: 100%;
    white-space: nowrap;
  }
`;

type Value = string | number;

type IProps = {
  field: string;
  data?: { text: string | JSX.Element; value: Value; label?: string }[];
  value?: Value[];
  onFinish: (values: any) => void;
  isLoading?: boolean;
  enableSearch?: boolean;
};

const DropdownSearchFilter: React.FC<IProps> = ({
  field,
  data = [],
  value,
  onFinish,
  isLoading = false,
  enableSearch = false,
}) => {
  const { messages } = useIntl();
  const [form] = Form.useForm();
  const [selectedValues, setSelectedValues] = useState<Value[]>([]);
  const [searchText, setSearchText] = useState("");

  const filteredData = useMemo(() => {
    if (!enableSearch || !searchText.trim()) {
      return data;
    }

    const searchLower = searchText.toLowerCase().trim();
    return data.filter((item) => {
      const textMatch =
        typeof item.text === "string"
          ? item.text.toLowerCase().includes(searchLower)
          : false;
      const labelMatch = item.label
        ? item.label.toLowerCase().includes(searchLower)
        : false;
      return textMatch || labelMatch;
    });
  }, [data, searchText, enableSearch]);

  return (
    <Form
      form={form}
      name={`dropdown_search_filter_form_${field}`}
      autoComplete="off"
      style={{ padding: "16px" }}
      className="table-action-form"
      initialValues={{
        value: Array.isArray(value) ? value : value ? [value] : [],
      }}
      onFinish={() => {
        onFinish({ field, value: selectedValues });
      }}
    >
      {enableSearch && (
        <Form.Item style={{ marginBottom: 10 }}>
          <Input
            allowClear
            size="middle"
            placeholder={messages["common.search"] as string}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            prefix={<span style={{ marginRight: 8 }}>🔍</span>}
          />
        </Form.Item>
      )}

      <Form.Item style={{ marginBottom: 10 }} name="value">
        <Spin spinning={isLoading} tip={messages["common.loading"] as string}>
          <div
            style={{
              maxHeight: "300px",
              overflowY: "auto",
              overflowX: "hidden",
              paddingRight: "4px",
            }}
            className="dropdown-filter-scroll"
          >
            {isLoading ? (
              <div style={{ padding: "20px", textAlign: "center" }}>
                <Spin />
              </div>
            ) : filteredData && filteredData.length > 0 ? (
              <Checkbox.Group onChange={(e) => setSelectedValues(e)}>
                <StyledFlex
                  style={{ flexDirection: "column", alignItems: "start" }}
                >
                  {filteredData.map((d) => (
                    <StyledCheckbox key={d.value} value={d.value}>
                      {d.text}
                    </StyledCheckbox>
                  ))}
                </StyledFlex>
              </Checkbox.Group>
            ) : (
              <div style={{ padding: "20px", textAlign: "center" }}>
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    enableSearch && searchText.trim()
                      ? ((messages["common.noSearchResults"] ||
                          messages["common.noDataAvailable"] ||
                          messages["common.noData"]) as string)
                      : ((messages["common.noDataAvailable"] ||
                          messages["common.noData"]) as string)
                  }
                />
              </div>
            )}
          </div>
        </Spin>
      </Form.Item>

      <Form.Item style={{ marginBottom: 0 }}>
        <StyledFlex style={{ justifyContent: "space-between" }}>
          <Button
            data-testid="reset"
            htmlType="reset"
            type="link"
            onClick={() => onFinish({ field, value: undefined })}
          >
            {messages["common.reset"] as string}
          </Button>
          <Button
            data-testid="submit"
            htmlType="submit"
            type="primary"
            loading={isLoading}
            disabled={isLoading}
          >
            {messages["common.submit"] as string}
          </Button>
        </StyledFlex>
      </Form.Item>
    </Form>
  );
};

export default DropdownSearchFilter;
